import { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { updateUserProfile, getUserData } from '../services/authService';
import { handlePostLoginRedirect } from '../services/clientService';
import type { UpdateProfileData } from '../services/authService';
import './Register.css';

function CompleteProfile() {
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    documento: '',
    email: '',
    domicilio: '',
    celular: '',
    birthdate: '',
    genero: '',
  });
  //hola
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string>('');

  useEffect(() => {
    // Pre-fill email from stored user data
    const userData = getUserData();
    if (userData?.email) {
      setFormData((prev) => ({
        ...prev,
        email: userData.email || '',
      }));
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData((prev) => ({
      ...prev,
      celular: value || '',
    }));
    // Clear error for phone field
    if (errors.celular) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.celular;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.apellido.trim()) {
      newErrors.apellido = 'El apellido es requerido';
    }

    if (!formData.documento.trim()) {
      newErrors.documento = 'El documento es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email)) {
      newErrors.email = 'Correo electrónico inválido';
    }

    if (!formData.celular.trim()) {
      newErrors.celular = 'El celular es requerido';
    }

    if (!formData.genero) {
      newErrors.genero = 'El género es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    
    if (validateForm()) {
      setLoading(true);
      try {
        const profileData: UpdateProfileData = {
          name: formData.nombre,
          surname: formData.apellido,
          document_number: formData.documento,
          email: formData.email,
          address: formData.domicilio,
          phone_number: formData.celular,
          birth_date: formData.birthdate,
          gender: formData.genero.charAt(0) as 'F' | 'M' | 'O', // Send only first character: F, M, or O
        };
        
        await updateUserProfile(profileData);

        // After completing profile, redirect to client domain or selector
        handlePostLoginRedirect();
      } catch (error) {
        console.error('Profile update error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        setApiError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="register-container">
      <div className="register-box">
        <div className="register-header">
          <h1 className="register-title">Completar Perfil</h1>
          <p className="register-subtitle">Complete sus datos para continuar</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nombre">Nombre *</label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Juan"
                disabled={loading}
                className={`form-input ${errors.nombre ? 'error' : ''}`}
              />
              {errors.nombre && <span className="error-text">{errors.nombre}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="apellido">Apellido *</label>
              <input
                id="apellido"
                name="apellido"
                type="text"
                value={formData.apellido}
                onChange={handleChange}
                placeholder="Pérez"
                disabled={loading}
                className={`form-input ${errors.apellido ? 'error' : ''}`}
              />
              {errors.apellido && <span className="error-text">{errors.apellido}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="documento">Documento *</label>
            <input
              id="documento"
              name="documento"
              type="text"
              value={formData.documento}
              onChange={handleChange}
              placeholder="DNI, Pasaporte, etc."
              disabled={loading}
              className={`form-input ${errors.documento ? 'error' : ''}`}
            />
            {errors.documento && <span className="error-text">{errors.documento}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Correo Electrónico *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="ejemplo@correo.com"
              disabled={true}
              className="form-input"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="domicilio">Domicilio</label>
            <input
              id="domicilio"
              name="domicilio"
              type="text"
              value={formData.domicilio}
              onChange={handleChange}
              placeholder="Calle, Número, Ciudad"
              disabled={loading}
              className={`form-input ${errors.domicilio ? 'error' : ''}`}
            />
            {errors.domicilio && <span className="error-text">{errors.domicilio}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="celular">Celular *</label>
            <PhoneInput
              id="celular"
              international
              defaultCountry="AR"
              value={formData.celular}
              onChange={handlePhoneChange}
              placeholder="Ingrese número de teléfono"
              disabled={loading}
              className={`phone-input ${errors.celular ? 'error' : ''}`}
            />
            {errors.celular && <span className="error-text">{errors.celular}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="birthdate">Fecha de Nacimiento</label>
            <input
              id="birthdate"
              name="birthdate"
              type="date"
              value={formData.birthdate}
              onChange={handleChange}
              disabled={loading}
              className={`form-input ${errors.birthdate ? 'error' : ''}`}
            />
            {errors.birthdate && <span className="error-text">{errors.birthdate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="genero">Género *</label>
            <select
              id="genero"
              name="genero"
              value={formData.genero}
              onChange={handleChange}
              disabled={loading}
              className={`form-input ${errors.genero ? 'error' : ''}`}
            >
              <option value="">Seleccione...</option>
              <option value="Femenino">Femenino</option>
              <option value="Masculino">Masculino</option>
              <option value="Otro">Otro</option>
            </select>
            {errors.genero && <span className="error-text">{errors.genero}</span>}
          </div>

          {apiError && <div className="api-error-message">{apiError}</div>}

          <button type="submit" disabled={loading} className="register-button">
            {loading ? 'Guardando...' : 'Completar Perfil'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CompleteProfile;
